// ActionBlock — a single button that renders an Action by id.
//
// Lookup is by `actionId` against the host's ActionMap; if the id isn't
// known we log once and render nothing (the slot mapping might be older
// than the running bundle).
//
// Click semantics: if the action has `opensModal`, the host's openModal is
// invoked (the host wires it onto the action's `run` already in
// useHostActions — so calling `run()` covers both modal-opener and momentary
// verbs uniformly).

import { Icon } from "../components/Icon";
import {
  resolveContentSource,
  type ContentSource,
  type ContentSourceContext,
} from "./contentSource";
import type { BlockHostContext, BlockUniversalProps } from "./types";

export interface ActionBlockProps extends BlockUniversalProps {
  actionId: string;
  host: BlockHostContext;
  /** Optional visible caption. When set, the button renders this text next to
   *  (or below, for image cards) the icon. Tooltip + aria still come from the
   *  ActionDef.label so icon-only buttons (no actionLabel) stay accessible.
   *  Presence of this prop is the single signal for "show a label". */
  actionLabel?: string;
  /** Optional hover tooltip / aria-label override. When empty falls back to
   *  the visible label, then to the ActionDef.label. Lets the operator give
   *  an icon-only button a meaningful hover text without committing to
   *  rendering a visible caption. */
  actionTooltip?: string;
  /** Optional per-instance Material Symbols glyph override. */
  actionIcon?: string;
  /** Optional preview-image URL. When set, the button renders as an image
   *  card (image on top, label below) instead of the icon-only button.
   *  Used by the scene-looks presets so each preset is a thumbnail-driven
   *  picker rather than three identical paint-brush icons. */
  actionImageUrl?: string;
  /** Forwarded to the action's run() so verbs whose payload lives on the
   *  placing block (e.g. apply-scene-materials's dmatBindings) can read it.
   *  Kept opaque to the block layer — the action defines the prop schema. */
  blockProps?: Record<string, unknown>;
  /** Content source for the visible label (literal / dvar / from-*). */
  labelSource?: ContentSource;
  /** Content source for the tooltip / aria text. */
  tooltipSource?: ContentSource;
  /** Optional overrides for the confirmation dialog a destructive verb shows
   *  (reset, back-to-series). Each falls back to the ActionDef's built-in copy
   *  when empty, so the operator can retranslate / reword the confirm without
   *  the action losing its default. Only meaningful for verbs that confirm. */
  confirmTitle?: string;
  confirmMessage?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Live content-source context (series + selection), handed down by Slot. */
  sourceCtx?: ContentSourceContext;
}

// Element-heatmap keys for the conversion-relevant verbs; everything else
// falls back to a generic per-action key ("action.<id>"). Keys are semantic,
// not positional — see src/analytics/README.md.
const TRACK_KEY_BY_ACTION: Record<string, string> = {
  "open-cart": "cart.open",
  "render-pdf-download": "cart.pdf",
  "render-pdf-email": "cart.email",
  "render-pdf-email-send": "cart.email",
  "download-model": "cart.ar",
};

export function ActionBlock({
  actionId, themeVariant, className, host,
  actionLabel, actionTooltip, actionIcon, actionImageUrl, blockProps,
  labelSource, tooltipSource,
  confirmTitle, confirmMessage, confirmLabel, cancelLabel,
  sourceCtx,
}: ActionBlockProps) {
  const action = host.actions.get(actionId);
  if (!action) {
    console.warn(`[SceneDesigner] action "${actionId}" not registered; ActionBlock skipped.`);
    return null;
  }
  // Per-block hide hook — stronger than isDisabledForBlock; the button
  // doesn't render at all so adjacent siblings (corner-button stacking,
  // tool-rail layout) reflow around it.
  if (action.isHiddenForBlock?.(blockProps) === true) {
    return null;
  }

  // Resolve the visible caption + tooltip through the shared content source so
  // either can be driven by the live series / selected product. A null result
  // (e.g. a from-* source with no live value) falls back to the absent-label
  // behaviour: icon-only button, ActionDef.label for aria.
  const resolvedLabel = resolveContentSource(labelSource, actionLabel, sourceCtx ?? {});
  const resolvedTooltip = resolveContentSource(tooltipSource, actionTooltip, sourceCtx ?? {});
  const label = resolvedLabel || action.label;
  // Tooltip precedence: explicit (resolved) tooltip → visible label → ActionDef.label.
  // Icon-only buttons (no visible label) can still get a meaningful hover hint
  // via actionTooltip without committing to rendering a caption.
  const tooltip = resolvedTooltip || resolvedLabel || action.label;
  // Icon resolution. The operator's authored actionIcon is authoritative when
  // the prop is *present* (a string) — including an empty string, which means
  // "render no icon at all" (the operator cleared the field).
  //   actionIcon === undefined → inherit default (legacy data that never went
  //                              through the icon field)
  //   actionIcon === ""        → no icon
  //   actionIcon === "name"    → that glyph
  //
  // The one exception: the dashboard prefills the field with the action's
  // STATIC default glyph (it can't know the runtime-dynamic glyph). For verbs
  // with an iconForBlock hook (toggle-variant's open/close swap, camera-toggle),
  // that prefilled static value must not freeze out the dynamic glyph. So when
  // actionIcon is exactly the static default, we still let the hook win — the
  // operator left the default untouched. A different typed value is a real
  // override and wins outright.
  const dynamicIcon = action.iconForBlock?.(blockProps);
  let icon: string;
  if (actionIcon === undefined) {
    icon = dynamicIcon ?? action.icon;
  } else if (actionIcon === action.icon && dynamicIcon !== undefined) {
    // Untouched prefilled default + a live dynamic glyph → use the dynamic one.
    icon = dynamicIcon;
  } else {
    icon = actionIcon;
  }
  // Disabled when the verb's own enabled flag is false OR the per-block
  // predicate flags this specific instance as unusable (e.g. toggle-variant
  // when the selected product has no variant by the configured key).
  const disabled =
    action.enabled === false || action.isDisabledForBlock?.(blockProps) === true;
  const hasImage = !!actionImageUrl;
  const fire = () => {
    // opensModal first, then run — matches the contract documented in
    // ActionDef.run. Each existing modal-opener (help, open-cart,
    // send-email-from-cart, open-scene-looks) carries only opensModal and no
    // run; without firing openModal here those buttons did nothing.
    // blockProps is also stashed under the modal id so the modal's render
    // override can read per-instance data the placing block carries (e.g.
    // the render-scene modal reads its profile id this way).
    if (action.opensModal) host.openModal(action.opensModal, blockProps);
    action.run?.(blockProps);
  };
  const onClick = () => {
    if (disabled) return;
    // Confirm guard for destructive verbs (reset, back-to-series). The host
    // owns the dialog state — we just hand it a callback and let it decide
    // when to invoke fire(). Verbs without a confirm fall straight through.
    if (action.confirm) {
      // Operator-authored overrides win; each falls back to the ActionDef copy.
      host.openConfirm({
        title: confirmTitle?.trim() || action.confirm.title,
        message: confirmMessage?.trim() || action.confirm.message,
        confirmLabel: confirmLabel?.trim() || action.confirm.confirmLabel,
        cancelLabel: cancelLabel?.trim() || action.confirm.cancelLabel,
        onConfirm: fire,
      });
      return;
    }
    fire();
  };

  // Image-card variant: the button itself stays the focus target so keyboard
  // + ARIA stay clean; visual chrome is the image-on-top, caption-below
  // arrangement (.sd-block-action--card). The icon-only variant is kept
  // for tool-rail use where there's no caption space.
  //
  // Inline text label rule: show the caption iff the operator set an
  // actionLabel. Icon-only buttons (tool rail) don't set one — their tooltip
  // + aria come from the ActionDef's own label instead. So label presence is
  // the single signal; no themeVariant special-casing.
  const showInlineLabel = !!resolvedLabel;

  const classes = [
    "sd-block-action",
    hasImage ? "sd-block-action--card" : null,
    className,
  ].filter(Boolean).join(" ");

  return (
    <button
      type="button"
      className={classes}
      data-action-id={actionId}
      data-track-key={TRACK_KEY_BY_ACTION[actionId] ?? `action.${actionId}`}
      data-active={action.active ? "true" : undefined}
      data-variant={themeVariant}
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      aria-label={tooltip}
      aria-pressed={action.active ? true : undefined}
    >
      {hasImage ? (
        <>
          <span className="sd-block-action__image">
            <img src={actionImageUrl} alt="" draggable={false} />
          </span>
          {resolvedLabel ? <span className="sd-block-action__label">{resolvedLabel}</span> : null}
        </>
      ) : showInlineLabel ? (
        // Labelled button (e.g. the cart's "Download PDF" pill). Shown whenever
        // the operator set an actionLabel. The icon is optional — an empty
        // actionIcon ("no icon") renders the caption alone (a text button).
        <>
          {icon ? <Icon name={icon} size={20} /> : null}
          <span className="sd-block-action__label">{resolvedLabel}</span>
        </>
      ) : (
        // Icon-only button (tool rail). With no icon there's nothing visible to
        // render, but the button keeps its tooltip / aria-label.
        icon ? <Icon name={icon} size={20} /> : null
      )}
    </button>
  );
}
